// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { Command } from "graphql/api";
import { DataState } from "types";

export interface ISchedulerItem {
  lastRequest?: number; // ms
  lastData?: number; // ms
}

export interface IPendingCommand {
  timestampMs: number;
  seqNr: number; // derived from timestamp, but some modulo to keep inside int data range
  command: Command;
  cmdArgs: any; // depends on the command
}

export enum AppState {
  INIT = 0, // startup state
  LOGGED_OUT = 1, // application files loaded, sagas initialized, but not logged in
  LOGGED_IN = 2, // logged in, but no site selected
  SITE_LIST_LOADED = 3, // site and user list loaded
  SITE_SELECTED = 4, // site selected, but no parameter/charger list loaded
  PARAMETER_LOADED = 5, // parameter, charger list, rfids, tariffs, and open charging sessions loaded
  LIVE_DATA_SUBSCRIBED = 6, // trendinfo ready, subscribed to enline and charger live data
  READY = 7, // enline live data received
}

/* Scheduler states */
export interface IStateScheduler {
  appState: AppState;
  isInBackground: boolean;
  login: ISchedulerItem;
  loginDataState: DataState;
  mfaDataState: DataState; // TOTP enrollment handling on the user settings page
  siteList: ISchedulerItem;
  userListBasic: ISchedulerItem;
  userListExt: ISchedulerItem;
  companyList: ISchedulerItem;
  reportList: ISchedulerItem;
  parameter: ISchedulerItem;
  parameterDataState: DataState;
  trendLoading: ISchedulerItem;
  trendLoadingState: DataState;
  trendLoadingProgress: number; // 0..1
  ioTrendLoading: ISchedulerItem;
  isIoTrendLoading: boolean;
  allData: ISchedulerItem;
  pushRegister: ISchedulerItem; // cloud: live-data registration / proxy token refresh
  alarm: ISchedulerItem;
  controllerData: ISchedulerItem;
  setOutput: ISchedulerItem;
  saveState: DataState; // save state for arbitraty data items
  pendingCommand: IPendingCommand | undefined;
  isOffline: boolean;
  liveViaProxy: boolean; // cloud build: live data + commands run via the controller proxy
  ocppChargerList: ISchedulerItem;
  ocppConnectorData: ISchedulerItem;
  ocppRfidListBasic: ISchedulerItem;
  ocppRfidListExt: ISchedulerItem;
  ocppTariffList: ISchedulerItem;
  pendingOcppSessionState: ISchedulerItem;
  ocppSessionState: DataState;
  chargingSession: ISchedulerItem;
  chargingPower: ISchedulerItem;
  fileUploadLoading: ISchedulerItem;
  isFileUploadLoading: boolean;
}

/* initial state */
export const INITIAL_STATE: IStateScheduler = {
  appState: AppState.INIT,
  isInBackground: false,
  login: {},
  loginDataState: DataState.EMPTY,
  mfaDataState: DataState.EMPTY,
  siteList: {},
  userListBasic: {},
  userListExt: {},
  companyList: {},
  reportList: {},
  parameter: {},
  parameterDataState: DataState.EMPTY,
  trendLoading: {},
  trendLoadingState: DataState.EMPTY,
  trendLoadingProgress: 0,
  ioTrendLoading: {},
  isIoTrendLoading: false,
  allData: {},
  pushRegister: {},
  alarm: {},
  controllerData: {},
  setOutput: {},
  saveState: DataState.EMPTY,
  pendingCommand: undefined,
  isOffline: false,
  liveViaProxy: false,
  ocppChargerList: {},
  ocppConnectorData: {},
  ocppRfidListBasic: {},
  ocppRfidListExt: {},
  ocppTariffList: {},
  pendingOcppSessionState: {},
  ocppSessionState: DataState.EMPTY,
  chargingSession: {},
  chargingPower: {},
  fileUploadLoading: {},
  isFileUploadLoading: false,
};
